import React, { useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { formatDistanceToNowStrict, fromUnixTime } from "date-fns";

import { RBACAccessLevel, RBACDomain } from "@/access-control/types";
import { Badge } from "@/design-system/components/Badge/Badge";
import { Banner } from "@/design-system/components/Banner/Banner";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Typography } from "@/design-system/components/Typography/Typography";
import statusMetadata from "@/destinations/Traces/statusMetadata";
import { IngestTraceRow } from "@/destinations/types";
import { useUserHasAccess } from "@/hooks/useUserHasAccess";
import { makeRequest } from "@/util";
import { WizardLink } from "@/wizards/framework/WizardLink";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import {
  classifyTrace,
  DeliveryOutcome,
  getDeliveryWindowStart,
  summarizeDeliveries,
} from "../delivery";
import { EnableDestinationStepProps } from "../stepProps";
import { useHasWaited } from "../useHasWaited";

const POLL_INTERVAL_MS = 15_000;
const ERROR_POLL_INTERVAL_MS = 60_000;
const GIVE_UP_AFTER_MS = 2 * 60_000;
const MAX_ROWS = 10;
// Traces skipped by design (the destination doesn't take this event type)
// are noise when checking that a newly enabled destination receives events.
const HIDDEN_STATUSES = new Set(["integrations_object_skipped"]);

const outcomeBadges: Record<
  DeliveryOutcome,
  { variant: "success" | "warning" | "error" | "info"; label?: string }
> = {
  delivered: { variant: "success" },
  retrying: { variant: "warning", label: "Retrying" },
  notSent: { variant: "info" },
  warning: { variant: "warning" },
  failed: { variant: "error" },
};

function useRecentTraces({
  destinationID,
  windowStart,
  enabled,
  stopPolling,
}: {
  destinationID: string | undefined;
  windowStart: number;
  enabled: boolean;
  stopPolling: boolean;
}) {
  return useQuery(
    ["enable_destination_wizard_traces", destinationID, windowStart],
    async () => {
      const response = await makeRequest<{ traces: IngestTraceRow[] }>(
        "list_destination_traces",
        {
          dest_id: destinationID,
          filters: {
            date_type: "timerange",
            date_start: windowStart,
            date_end: Math.ceil(Date.now() / 1000) + 60,
          },
        },
      );
      return response.data.traces
        .filter((trace) => trace && !HIDDEN_STATUSES.has(trace.Status))
        .sort((a, b) => b.Time - a.Time);
    },
    {
      enabled: enabled && !!destinationID,
      retry: false,
      refetchInterval: (_data, query) =>
        stopPolling
          ? false
          : query.state.status === "error"
            ? ERROR_POLL_INTERVAL_MS
            : POLL_INTERVAL_MS,
    },
  );
}

export function DeliveryStep({
  data,
  context,
  updateData,
  goNext,
}: EnableDestinationStepProps) {
  const {
    saasDestination: destination,
    configuredDestination,
    activeConsentManager,
  } = context;
  const name = destination?.name ?? "the destination";
  const Name = destination?.name ?? "The destination";
  const canReadPHI = useUserHasAccess(RBACDomain.phi, RBACAccessLevel.read);
  const lastToggledAt = configuredDestination?.last_toggled_at;
  const windowStart = useMemo(
    () =>
      getDeliveryWindowStart(
        lastToggledAt,
        Math.floor(Date.now() / 1000),
        data.credentialsSavedAt,
      ),
    [lastToggledAt, data.credentialsSavedAt],
  );
  const {
    data: traces,
    isLoading,
    isError,
  } = useRecentTraces({
    destinationID: configuredDestination?.id,
    windowStart,
    enabled: canReadPHI === true,
    stopPolling: !!data.deliveredAt,
  });

  const summary = useMemo(() => summarizeDeliveries(traces ?? []), [traces]);
  const hasTraces = !!traces?.length;
  const isWaiting = canReadPHI === true && !summary.delivered;
  const waitedTooLong = useHasWaited(GIVE_UP_AFTER_MS, isWaiting);

  useEffect(() => {
    if (summary.delivered && !data.deliveredAt) {
      updateData({ deliveredAt: Date.now() });
    }
  }, [summary.delivered, data.deliveredAt, updateData]);

  const delivered = summary.delivered || !!data.deliveredAt;

  function renderResults() {
    if (canReadPHI === null) {
      return <LoadingSpinner className="size-6" />;
    }
    if (canReadPHI === false) {
      return (
        <Banner type="warning">
          Delivery results can include PHI, and you don’t have permission to
          view it. Ask an admin to check delivery, or continue without checking.
        </Banner>
      );
    }
    if (isLoading) {
      return <LoadingSpinner className="size-6" />;
    }
    return (
      <>
        {isError && (
          <Banner type="error">
            We couldn’t load delivery results. We’ll try again in a minute.
          </Banner>
        )}
        {delivered && (
          <Banner type="success">{Name} is accepting events.</Banner>
        )}
        {summary.failed && (
          <Banner type="error">
            Some events failed to send. Open event verification to see why.
          </Banner>
        )}
        {summary.warning && (
          <Banner type="warning">
            {Name} accepted some events with warnings.
          </Banner>
        )}
        {summary.notSentReasons.length > 0 && (
          <Banner type="info" kind="note">
            Freshpaint didn’t send some events:{" "}
            {summary.notSentReasons.join(", ")}.
          </Banner>
        )}
        {!delivered && !isError && (
          <div className="flex items-center gap-3">
            <LoadingSpinner className="size-5" />
            <Typography variant="body-sm" color="subtle">
              {hasTraces
                ? `Waiting for ${name} to accept an event…`
                : "No delivery attempts yet. Trigger an event and give it a minute."}
            </Typography>
          </div>
        )}
        {!delivered && waitedTooLong && (
          <Banner type="warning">
            <div>
              No events have been delivered yet. Common causes:
              <ul className="mt-1 list-disc pl-4">
                <li>
                  None of the chosen events have fired since {name} was turned
                  on.
                </li>
                {activeConsentManager && (
                  <li>
                    Visitors haven’t consented to {name}’s consent category.
                  </li>
                )}
                <li>
                  {Name} rejected the credentials. Event verification shows its
                  response.
                </li>
              </ul>
            </div>
          </Banner>
        )}
        {hasTraces && (
          <ul className="border-borderSubtle divide-borderSubtle divide-y rounded-lg border">
            {traces.slice(0, MAX_ROWS).map((trace) => {
              const badge = outcomeBadges[classifyTrace(trace)];
              return (
                <li
                  key={trace.ID}
                  className="flex items-center justify-between gap-4 px-4 py-2"
                >
                  <span className="text-label-sm text-textPrimary truncate">
                    {trace.EventDefName || trace.EventType}
                  </span>
                  <span className="flex shrink-0 items-center gap-3">
                    <Badge
                      text={
                        badge.label ??
                        statusMetadata.get(trace.Status)?.label ??
                        trace.Status
                      }
                      variant={badge.variant}
                    />
                    <span className="text-body-xs text-textTertiary">
                      {formatDistanceToNowStrict(fromUnixTime(trace.Time), {
                        addSuffix: true,
                      })}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </>
    );
  }

  return (
    <WizardStepLayout
      title={`Check delivery to ${name}`}
      description={`Freshpaint records a sample of its attempts to send events. This step shows attempts made after ${name} was turned on, up to an hour back, and refreshes on its own.`}
      primaryAction={{
        label: delivered ? "Continue" : "Continue without checking",
        onClick: goNext,
      }}
    >
      {renderResults()}
      {destination && configuredDestination && (
        <Typography variant="body-sm">
          <WizardLink
            to={`${destination.configPageUrl()}/${configuredDestination.id}/event-verification`}
            className="text-textActionSelected cursor-pointer underline"
          >
            Open event verification
          </WizardLink>{" "}
          for full payloads and responses.
        </Typography>
      )}
    </WizardStepLayout>
  );
}
